import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';

import {
  getChangedEquipListByQr,
  useUpdateQrVersionMutate,
} from 'api/deviceApi';
import { equipQrKeys } from 'api/quires/queryKeys';

import { generatePopupContent } from '../../../util/commonUtil';
import QrConfirmModal from './QrConfirmModal';

type QrcodeModalProps = {
  factoryId: string;
  equipIdList: number[];
  isOpen: boolean;
  onClose: () => void;
  onClick: () => void;
  confirmIsOpen: boolean;
};

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: 0,
    border: 0,
  },
};

export default function QrcodeModal({
  factoryId,
  equipIdList,
  isOpen,
  onClose,
  onClick,
  confirmIsOpen,
}: QrcodeModalProps) {
  const { t } = useTranslation();
  const popupRef = useRef<HTMLDivElement>(null);

  const updateQrVersion = useUpdateQrVersionMutate();
  const onPrint = () => {
    const printContents = popupRef.current?.innerHTML;
    if (!printContents) {
      return;
    }

    updateQrVersion.mutate(
      { factoryId, equipIdList },
      {
        onSuccess: (resp) => {
          if (resp.code === 200) {
            const windowObject = window.open(
              '',
              'PrintWindow',
              'width=800, height=600, top=200, left=200, toolbars=no, scrollbars=no, status=no, resizable=no',
            );

            if (!windowObject) {
              return;
            }

            windowObject.document.open();
            windowObject.document.title = `설비QR_${new Date().toString()}`;
            windowObject.document.write(...generatePopupContent(printContents));
            windowObject.document.close();

            windowObject.onload = () => {
              setTimeout(() => windowObject.print(), 500);

              windowObject.onafterprint = () => {
                windowObject.close();
              };
            };
          } else {
            alert(resp.message);
          }
        },
      },
    );
  };

  const qrCodeListRef = useRef([]);
  const { data: equipList, refetch: refetchEquip } = useQuery(
    equipQrKeys.all,
    () => getChangedEquipListByQr({ factoryId, equipIdList }),
    {
      enabled: false,
    },
  );

  useEffect(() => {
    if ((!equipIdList || equipIdList.length) === 0) {
      return;
    }

    if (isOpen) {
      refetchEquip();
    }
  }, [equipIdList, refetchEquip, isOpen]);

  useEffect(() => {
    if (!equipList || !equipList.list) {
      return;
    }

    qrCodeListRef.current = qrCodeListRef.current.slice(
      0,
      equipList.list.length,
    );
  }, [equipList]);

  return (
    <ReactModal isOpen={isOpen} style={customStyles} onRequestClose={onClose}>
      <div id={'popup-wrap'}>
        <div className={'popup-wrap'} id={'qr-detail'}>
          <div className={'popup-head'}>
            <p className={'poptit'}>{t('device.qrPrint')}</p>
            <button
              type={'button'}
              className={'red_white_btn noPrint'}
              onClick={onClick}
            >
              {t('lotoLog.print')}
            </button>
          </div>
          <div className={'popup-cont'} ref={popupRef}>
            <div className={'qr-cont'}>
              <div className={'cont-wrap qrcode-popup'}>
                <div className={'qr-wrap'}>
                  {equipList &&
                    equipList.list?.map((equip) => (
                      <div className={'qr-box'} key={equip.code}>
                        <b className={'title'}>{'LOTO'}</b>
                        <b className={'sub'}>{equip.section ?? ''}</b>
                        <div className={'qr'}>
                          <QRCodeSVG value={equip.code} size={130} />
                        </div>
                        <b className={'sub'}>{equip.itemCode}</b>
                        <b className={'sub pad20f'}>{equip.name}</b>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <QrConfirmModal
        onClick={onPrint}
        isOpen={confirmIsOpen}
        onClose={onClose}
      />
    </ReactModal>
  );
}
